import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { HelpCircle, MessageSquare, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

const statusConfig = {
  open: { color: '#2563eb', bg: '#dbeafe', icon: Clock, label: 'Open' },
  in_progress: { color: '#f59e0b', bg: '#fef3c7', icon: MessageSquare, label: 'In Progress' },
  resolved: { color: '#059669', bg: '#d1fae5', icon: CheckCircle, label: 'Resolved' },
  closed: { color: '#6b7280', bg: '#f3f4f6', icon: XCircle, label: 'Closed' }
};

const faqData = [
  {
    question: 'How do I enroll in a new course?',
    answer: 'Courses are assigned by your teachers or administrators. Check your "My Courses" page to see all assigned courses. If you believe you should have access to a course, contact your teacher or admin.'
  },
  {
    question: 'What is the minimum score to pass a quiz?',
    answer: 'You need to score at least 30% on each quiz to pass and unlock the next lesson. Don\'t worry - you can retake quizzes multiple times until you pass!'
  },
  {
    question: 'How do I earn XP points?',
    answer: 'You earn 10 XP points for each lesson you complete successfully. Complete more lessons to climb the leaderboard!'
  },
  {
    question: 'What happens if I miss a day in my streak?',
    answer: 'Your daily streak resets if you don\'t log in for a day. But don\'t worry - you can start building it again from day 1!'
  },
  {
    question: 'How do I update my profile information?',
    answer: 'Go to "My Profile" from the sidebar. You can edit your name, mobile number, school name, and city. Your email address cannot be changed.'
  },
  {
    question: 'Can I watch a lesson video multiple times?',
    answer: 'Yes! You can watch lesson videos as many times as you need. Learning at your own pace is encouraged.'
  }
];

export default function Help({ user, onLogout }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'technical'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/help-tickets`, { headers: getAuthHeaders() });
      setTickets(res.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/help-tickets`, formData, { headers: getAuthHeaders() });
      toast.success('Ticket submitted successfully!');
      setFormData({ subject: '', description: '', category: 'technical' });
      setShowForm(false);
      fetchTickets();
    } catch (error) {
      console.error('Failed to submit ticket:', error);
      toast.error('Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout} currentPage="help"><div className="p-8">Loading...</div></Layout>;
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="help">
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-2" style={{ color: '#d86a2e' }}>❓ Help & Support</h1>
            <p className="text-lg text-gray-600">We're here to help you succeed</p>
          </div>
          <div className="hidden lg:block">
            <div className="text-6xl">💬</div>
          </div>
        </div>

        {/* Quick Actions */}
        <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #fff5eb 0%, #ffe8d6 100%)' }}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-1" style={{ color: '#d86a2e' }}>Need help?</h3>
                <p className="text-gray-600">Check our FAQ below or submit a support ticket</p>
              </div>
              <Button
                onClick={() => setShowForm(!showForm)}
                className="font-semibold"
                style={{ background: '#ff9d5c' }}
              >
                {showForm ? 'Cancel' : '+ New Ticket'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Ticket Form */}
        {showForm && (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold mb-4" style={{ color: '#d86a2e' }}>Submit a Support Ticket</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="technical">Technical Issue</option>
                    <option value="course">Course Related</option>
                    <option value="account">Account Issue</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Subject</label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief description of the issue"
                    className="focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please describe your issue in detail..."
                    rows={5}
                    className="focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full font-semibold"
                  style={{ background: '#ff9d5c' }}
                >
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* My Tickets */}
        {tickets.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold" style={{ color: '#d86a2e' }}>📋 My Support Tickets</h2>
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const config = statusConfig[ticket.status];
                const StatusIcon = config.icon;
                
                return (
                  <Card key={ticket.id} className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ background: config.bg }}
                        >
                          <StatusIcon className="w-6 h-6" style={{ color: config.color }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-bold">{ticket.subject}</h3>
                            <span
                              className="px-3 py-1 rounded-full text-xs font-semibold"
                              style={{ background: config.bg, color: config.color }}
                            >
                              {config.label}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-2">{ticket.description}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="px-2 py-1 rounded bg-gray-100">{ticket.category}</span>
                            <span>Created: {formatDate(ticket.created_at)}</span>
                            {ticket.resolved_at && <span>Resolved: {formatDate(ticket.resolved_at)}</span>}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold" style={{ color: '#d86a2e' }}>❔ Frequently Asked Questions</h2>
          <div className="space-y-3">
            {faqData.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: '#ffe8d6' }}
                    >
                      <HelpCircle className="w-5 h-5" style={{ color: '#d86a2e' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold mb-2" style={{ color: '#2d2d2d' }}>{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}